import React, { createContext, useContext, ReactNode } from 'react';
import { authService } from '../../features/auth/services/auth.service';
import { useAuthState } from '../../features/auth/hooks/useAuthState';
import { AuthState, LoginCredentials, SignupData, VerificationData, ResetPasswordData, InvitePasswordResetData } from '../../features/auth/types';

interface AuthContextType {
  authState: AuthState;
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  confirmSignup: (data: VerificationData) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (data: ResetPasswordData) => Promise<void>;
  resetPasswordWithTemporary: (data: InvitePasswordResetData) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState, checkAuthState, updateAuthState } = useAuthState();

  const login = async (credentials: LoginCredentials) => {
    await authService.login(credentials);
    await checkAuthState();
  };

  const signup = async (data: SignupData) => {
    await authService.signup(data);
  };

  const logout = async () => {
    await authService.logout();
    updateAuthState({ user: null, isAuthenticated: false });
  };

  const confirmSignup = async (data: VerificationData) => {
    await authService.confirmSignup(data);
  };

  const forgotPassword = async (email: string) => {
    await authService.forgotPassword(email);
  };

  const confirmForgotPassword = async (data: ResetPasswordData) => {
    await authService.confirmForgotPassword(data);
  };

  const resetPasswordWithTemporary = async (data: InvitePasswordResetData) => {
    await authService.resetPasswordWithTemporary(data);
    await checkAuthState();
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        signup,
        logout,
        confirmSignup,
        forgotPassword,
        confirmForgotPassword,
        resetPasswordWithTemporary,
        refreshUser: checkAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};